import { createTheme, responsiveFontSizes } from '@material-ui/core/styles'
import {
  BACKGROUND_COLOR,
  DISABLED_COLOR,
  ERROR_COLOR,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
  TEXT_COLOR,
  SUCCESS_COLOR,
  PRIMARY_COLOR_LIGHT,
  OUTLINE_GREY
} from './Colors'

const theme = responsiveFontSizes(
  createTheme({
    props: {
      MuiButtonBase: {
        disableRipple: true // Disable ripple on everything!
      },
      MuiButton: {
        disableElevation: true
      },
      MuiRadio: {
        color: 'primary'
      },
      MuiDialog: {
        scroll: 'body'
      }
    },
    overrides: {
      // Drawer styling
      MuiDrawer: {
        paperAnchorDockedLeft: {
          borderRightColor: '#EAECF0'
        }
      },

      // Global list item styling
      MuiListItem: {
        root: {
          color: '#344054',
          '&.Mui-selected': {
            backgroundColor: '#F2F4F7'
          },
          '&.Mui-selected:hover': {
            color: TEXT_COLOR,
            backgroundColor: '#F2F4F7'
          }
        },
        button: {
          '&:hover': {
            backgroundColor: '#F2F4F7',
            color: TEXT_COLOR,
            borderRadius: 6
          }
        }
      },
      // Global tab styling
      MuiTab: {
        root: {
          fontSize: '14px'
        }
      },
      // Global button styling
      MuiButton: {
        outlined: {
          borderColor: OUTLINE_GREY
        },
        label: {
          fontSize: '14px'
        }
      },
      // Global icon button styling
      MuiListItemIcon: {
        root: {
          color: '#667085',
          minWidth: '40px'
        }
      },
      // Global chip outline styling
      MuiChip: {
        root: {
          '& .MuiChip-outlined': {
            borderColor: '#D0D5DD'
          }
        }
      },
      //Global divider styling
      MuiDivider: {
        root: {
          backgroundColor: '#EAECF0'
        }
      },
      // Global input field styling
      MuiInputLabel: {
        root: {
          fontSize: '14px'
        }
      },
      MuiOutlinedInput: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D0D5DD'
          },
          '&:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D0D5DD'
          },
          '&:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(169, 161, 253, 0.5)'
          }
        }
      },
      MuiInputBase: {
        root: {
          border: '#D0D5DD',
          fontSize: '14px',
          lineHeight: '1.2rem'
        },
        input: {
          '&::placeholder': {
            opacity: 1,
            color: '#98A2B3'
          },
          fontSize: '14px'
        }
      },
      MuiSelect: {
        root: {
          fontSize: '14px'
        },
        select: {
          '&:active': {
            backgroundColor: '#F9FAFB'
          }
        }
      },
      MuiLinearProgress: {
        colorPrimary: {
          backgroundColor: '#EAECF0'
        },
        barColorPrimary: {
          backgroundColor: '#5148D7'
        }
      }
    },
    palette: {
      primary: {
        main: PRIMARY_COLOR,
        light: PRIMARY_COLOR_LIGHT
      },
      secondary: {
        main: SECONDARY_COLOR
      },
      success: {
        main: SUCCESS_COLOR,
        light: '#6CE9A6',
        dark: '#027A48',
        contrastText: '#FFFFFF'
      },
      info: {
        main: '#84CAFF',
        light: '#64B6F7',
        dark: '#175CD3',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#F79009',
        light: '#FEC84B',
        dark: '#B54708',
        contrastText: '#FFFFFF'
      },
      error: {
        main: ERROR_COLOR,
        light: '#FDA29B',
        dark: '#B42318',
        contrastText: '#FFFFFF'
      },
      text: {
        primary: TEXT_COLOR,
        disabled: DISABLED_COLOR
      },
      background: {
        default: BACKGROUND_COLOR
      },
      grey: {
        50: '#F9FAFB',
        100: '#F2F4F7',
        200: '#EAECF0',
        300: '#D0D5DD',
        400: '#98A2B3',
        500: '#667085',
        600: '#475467',
        700: '#344054',
        800: '#1D2939',
        900: '#101828',
        A100: '#F2F4F7',
        A200: '#EAECF0',
        A400: '#98A2B3',
        A700: '#344054'
      }
    },

    shape: {
      borderRadius: 8
    },
    shadows: [
      'none',
      '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
      '0px 1px 2px rgba(100, 116, 139, 0.12)',
      '0px 1px 4px rgba(100, 116, 139, 0.12)',
      '0px 1px 5px rgba(100, 116, 139, 0.12)',
      '0px 1px 6px rgba(100, 116, 139, 0.12)',
      '0px 2px 6px rgba(100, 116, 139, 0.12)',
      '0px 3px 6px rgba(100, 116, 139, 0.12)',
      '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
      '0px 5px 12px rgba(100, 116, 139, 0.12)',
      '0px 5px 14px rgba(100, 116, 139, 0.12)',
      '0px 5px 15px rgba(100, 116, 139, 0.12)',
      '0px 6px 15px rgba(100, 116, 139, 0.12)',
      '0px 7px 15px rgba(100, 116, 139, 0.12)',
      '0px 8px 15px rgba(100, 116, 139, 0.12)',
      '0px 9px 15px rgba(100, 116, 139, 0.12)',
      '0px 10px 15px rgba(100, 116, 139, 0.12)',
      '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
      '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
      '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
      '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
      '0px 25px 50px rgba(100, 116, 139, 0.25)',
      '0px 25px 50px rgba(100, 116, 139, 0.25)',
      '0px 25px 50px rgba(100, 116, 139, 0.25)',
      '0px 25px 50px rgba(100, 116, 139, 0.25)'
    ],
    typography: {
      fontFamily:
        '"InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.57
      },
      subtitle1: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.75
      },
      subtitle2: {
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.57
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.5px',
        lineHeight: 2.5,
        textTransform: 'uppercase'
      },
      caption: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.66
      },
      h1: {
        fontWeight: 700,
        fontSize: '3.5rem',
        lineHeight: 1.375,
        letterSpacing: '-0.02rem'
      },
      h2: {
        fontWeight: 700,
        fontSize: '3rem',
        lineHeight: 1.375,
        letterSpacing: '-0.02rem'
      },
      h3: {
        fontWeight: 700,
        fontSize: '2.25rem',
        lineHeight: 1.375,
        letterSpacing: '-0.02rem'
      },
      h4: {
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1.375,
        letterSpacing: '-0.02rem'
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.375
      },
      h6: {
        fontWeight: 500,
        fontSize: '1.125rem',
        lineHeight: 1.375
      },
      button: {
        textTransform: 'none',
        fontSize: '1rem'
      }
    }
  })
)

// TODO: Replace these manual overrides with a configurable shadow generation system
theme.shadows[1] =
  'rgba(145, 158, 171, 0.3) 0px 1px 1px, rgba(145, 158, 171, 0.1) 0px 1px 2px'
theme.shadows[2] =
  'rgba(145, 158, 171, 0.3) 0px 0px 2px 0px, rgba(145, 158, 171, 0.1) 0px 12px 24px'
// Inset shadow
theme.shadows[3] =
  '0 -2 0 hsla(0, 0%, 100%, .15), inset 0px 2px 2px hsla(0, 0%, 0%, 0.1)'

export default theme
